import React, { useCallback, useEffect } from "react";
import { useFormikContext } from "formik";
import { DishSubmitOutcome, MenuItemFormData } from "./DishUpdatePage";
import { useScrollToErrorMeMenu } from "@me-team/host/src/utils/useScrollToErrorMeMenu";
import { SaveCurrentChangesResult, useConfirmLanguageSwitch } from "../utils/hooks/useConfirmLanguageSwitch";
import { menuLanguageVersionVar } from "@me-team/host/main/menuLanguageVersions/state";
import { useReactiveVar } from "@apollo/client";
import ConfirmChangesModal from "../components/ConfirmChangesModal/ConfirmChangesModal";

interface CurrentMenuItemFormEffectsProps {
    pickedCategoryIds?: number[];
    languageSwitchRequestRef: React.MutableRefObject<((nextCode: string) => void) | null>;
    preventNavigateOnSaveRef: React.MutableRefObject<boolean>;
    submitOutcomeRef: React.MutableRefObject<DishSubmitOutcome>;
}

const CurrentMenuItemFormEffects: React.FC<CurrentMenuItemFormEffectsProps> = ({
    pickedCategoryIds,
    languageSwitchRequestRef,
    preventNavigateOnSaveRef,
    submitOutcomeRef,
}) => {
    const { setFieldValue, submitCount, errors, dirty, submitForm, isSubmitting, validateForm } =
        useFormikContext<MenuItemFormData>();

    useScrollToErrorMeMenu({ submitClickedCount: submitCount, errors });

    useEffect(() => {
        if (pickedCategoryIds) {
            setFieldValue("categories", pickedCategoryIds);
        }
    }, [pickedCategoryIds, setFieldValue]);

    const currentLanguageCode = useReactiveVar(menuLanguageVersionVar);

    const applyLanguage = useCallback((nextCode: string) => {
        menuLanguageVersionVar(nextCode);
    }, []);

    const saveCurrentChanges = useCallback(async (): Promise<SaveCurrentChangesResult> => {
        if (isSubmitting) {
            return { ok: false, reason: "submit" };
        }
        submitOutcomeRef.current = "pending";
        preventNavigateOnSaveRef.current = true;
        await submitForm();
        const validationErrors = await validateForm();
        const hasValidationErrors = Object.keys(validationErrors || {}).length > 0;
        if (hasValidationErrors) {
            preventNavigateOnSaveRef.current = false;
            return { ok: false, reason: "validation" };
        }
        const submitOutcome = submitOutcomeRef.current as DishSubmitOutcome;
        if (submitOutcome !== "saved") {
            preventNavigateOnSaveRef.current = false;
            return { ok: false, reason: "submit" };
        }
        return { ok: true };
    }, [isSubmitting, preventNavigateOnSaveRef, submitForm, submitOutcomeRef, validateForm]);

    const { requestLanguageChange, modalProps } = useConfirmLanguageSwitch({
        currentLanguageCode,
        isDirty: dirty,
        saveCurrentChanges,
        applyLanguage,
    });

    useEffect(() => {
        languageSwitchRequestRef.current = requestLanguageChange;
        return () => {
            if (languageSwitchRequestRef.current === requestLanguageChange) {
                languageSwitchRequestRef.current = null;
            }
        };
    }, [languageSwitchRequestRef, requestLanguageChange]);

    return (
        <ConfirmChangesModal
            show={modalProps.show}
            isSaving={modalProps.isSaving}
            onClose={modalProps.onClose}
            onSave={modalProps.onSave}
            onDontSave={modalProps.onDontSave}
        />
    );
};

export default CurrentMenuItemFormEffects;
